import React from 'react';
import { connect } from 'react-redux';
import { imageDataSet, imageDataClear } from '../../actions/imageData';
import { clearFormData, clearCardData } from '../../actions/addInsurance';
import { setRuntimeVariable } from '../../actions/runtime';
import AddInsurance from '../../components/AddInsurance';
import {
  ACUANT_POST,
  SUBMIT_INSURANCE_FORM,
  FETCH_INSURERS_LIST,
  SUBMIT_DENTAL_INSURANCE_FORM,
  INSURANCE_IMAGE_UPLOAD,
  DENTAL_SKIP_COPY_VARIABLE,
} from '../../constants';
import { getCurrentUser } from '../../core/util/currentUser';
import { getNextRoute } from '../../components/AddInsurance/util/routing';
import { getInitialValues } from '../../components/AddInsurance/util/insuranceForm';
import { analyticsTrackEvent } from '../../core/analytics';
import { INSURANCE_FILE_UPLOAD } from '../../core/analytics/events';
import ExifImageLoader, { DEFAULT_JPEG_QUALITY } from '../../core/util/ExifImageLoader';
import { createBlob } from '../../core/util/image';
import { INSURANCE_TYPES } from '../../constants';
import AddDentalInsurance from '../../components/AddDentalInsurance';
import { FETCH_MARKETPLACE_INSURERS } from '../../sagas/marketplaceInsurers';
import { safeGet } from '../../core/util/object';
import { unsetCookie } from '../../core/util/cookies';
import { CookieNames } from '../../config';

export const JPEG_QUALITY = 0.6;
export const FILE_SIZE_THRESHOLD = 2000000;

const mapStateToProps = (state, ownProps) => ({
  location: state.location,
  insurers: state.insurers,
  newBooking: state.newBooking,
  accountSummary: state.account.summary,
  initialValues: getInitialValues(state),
  insuranceCoverage: state.npi,
  addInsuranceFlow: ownProps.origin,
  currentUser: getCurrentUser(state),
  isEditFlow: state.runtime.isEditFlow,
  marketplaceInsurers: state.marketplaceInsurers.insurers || [],
  requestedInsurance: safeGet(state.searchPreferences)('requestedInsurance'),
  insuranceCard: {
    front: {
      base64: state.imageData.front,
      scanStarted: state.addInsurance.front.scanStarted,
      scanCompleted: state.addInsurance.front.scanCompleted,
      error: state.addInsurance.front.error,
    },
    back: {
      base64: state.imageData.back,
      scanStarted: state.addInsurance.back.scanStarted,
      scanCompleted: state.addInsurance.back.scanCompleted,
      error: state.addInsurance.back.error,
    },
  },
  passThrough: getNextRoute(ownProps.origin, ownProps.query),
  showSkipCopy: state.runtime[DENTAL_SKIP_COPY_VARIABLE],
});

export const healthDispatch = dispatch => ({
  handleFileChange: imgType => async event => {
    const files = [...event.target.files];
    if (!files || !files.length > 0) return;
    const imageFile = files[0];
    analyticsTrackEvent(INSURANCE_FILE_UPLOAD, {
      fileSizeBytes: imageFile.size,
    });
    const jpegQuality = imageFile.size > FILE_SIZE_THRESHOLD ? JPEG_QUALITY : DEFAULT_JPEG_QUALITY;

    const imageLoader = new ExifImageLoader(imageFile);
    const rawJpeg = await imageLoader.getOrientedImage(jpegQuality);
    dispatch(imageDataSet(rawJpeg, imgType));

    const fileBlob = createBlob(rawJpeg);

    dispatch({
      type: ACUANT_POST,
      imgType,
      imageFile: fileBlob,
    });
  },

  clearData: () => {
    dispatch(clearCardData());
    dispatch(imageDataClear());
    dispatch(clearFormData());
  },

  clearCardData: () => {
    // mobile use case, go back to re-upload card, needs to clear only card data
    dispatch(clearCardData());
    dispatch(imageDataClear());
  },

  fetchInsurersList: stateCode =>
    dispatch({
      type: FETCH_INSURERS_LIST,
      stateCode,
    }),

  clearEditFlow: () => dispatch(setRuntimeVariable({ name: 'isEditFlow', value: false })),

  submitInsuranceInformation: (flow, form, location, currentUser, insurers) => {
    unsetCookie(CookieNames.INSURANCE_TUTORIAL);
    dispatch(setRuntimeVariable({ name: 'isEditFlow', value: false }));
    dispatch({
      type: SUBMIT_INSURANCE_FORM,
      flow,
      form,
      location,
      currentUser,
      insurers,
    });
  },
});

const dentalDispatch = dispatch => ({
  handleFileChange: imgType => event => {
    const files = [...event.target.files];
    if (!files || !files.length > 0) return;
    const imageFile = files[0];
    analyticsTrackEvent(INSURANCE_FILE_UPLOAD, {
      fileSizeBytes: imageFile.size,
    });

    dispatch({
      type: INSURANCE_IMAGE_UPLOAD,
      imgType,
      imageFile,
    });
  },

  fetchMarketplaceInsurers() {
    dispatch({ type: FETCH_MARKETPLACE_INSURERS });
  },

  clearData: () => {
    dispatch(clearCardData());
    dispatch(imageDataClear());
    dispatch(clearFormData());
  },

  clearCardData: () => {
    // mobile use case, go back to re-upload card, needs to clear only card data
    dispatch(clearCardData());
    dispatch(imageDataClear());
  },

  fetchInsurersList: stateCode =>
    dispatch({
      type: FETCH_INSURERS_LIST,
      stateCode,
    }),

  submitInsuranceInformation: (
    flow,
    form,
    location,
    currentUser,
    insurers,
    insuranceType = 'medical'
  ) => {
    const getEventType = () => {
      switch (insuranceType) {
        case INSURANCE_TYPES.vision:
          return SUBMIT_DENTAL_INSURANCE_FORM;
        case INSURANCE_TYPES.dental:
          return SUBMIT_DENTAL_INSURANCE_FORM;
        case INSURANCE_TYPES.medical:
        default:
          return SUBMIT_INSURANCE_FORM;
      }
    };

    dispatch({
      type: getEventType(),
      flow,
      form,
      location,
      currentUser,
      insurers,
    });
  },
});

export const ConnectedAddHealthInsurance = connect(
  mapStateToProps,
  healthDispatch
)(AddInsurance);

const ConnectedAddDentalInsurance = connect(
  mapStateToProps,
  dentalDispatch
)(AddDentalInsurance);

export default context => {
  const { params, query } = context;
  const insuranceType = params.insuranceType || INSURANCE_TYPES.medical;
  const Connected =
    insuranceType === INSURANCE_TYPES.dental
      ? ConnectedAddDentalInsurance
      : ConnectedAddHealthInsurance;

  return {
    chunks: ['addInsurance'],
    component: <Connected origin={params.origin} query={query} />,
    accountWrapper: {
      loginRequired: false,
      nonBlocking: true,
    },
  };
};
